import React, { useEffect, useState, useCallback } from "react";
import Layout from "../components/layoutBlack";
import axios from "axios";
import { config } from "../utils/constants";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumbs from "../components/breadcrumbs";
import PromoOffer from "../components/promoOffer";
import { useRandomScrollPromo } from "../hooks/useRandomScrollPromo";
import { Helmet } from "react-helmet-async";

const BRAND = "#001f3f";

const Shop = () => {
  const REACT_APP_API_URL = config.url.REACT_APP_API_URL;
  const REACT_API_IMAGE_URL = config.url.REACT_API_IMAGE_URL;
  const navigate = useNavigate();
  const ogImage = "https://molu.co/og/molu-banner-1200x630.png";

  const [families, setFamilies] = useState([]);
    const [promoOpen, setPromoOpen] = useState(false);
  
    // Hook will call this to open randomly
    const onTrigger = useCallback(() => setPromoOpen(true), []);
  
    useRandomScrollPromo({
      onTrigger,
      // Tune these as you like:
      minTimeMs: 1000,           // wait at least 8s on page
      minScrollPx: 300,           // and 800px of scroll
      probability: .8,          // 80% chance when we check
      checkThrottleMs: 1500,      // throttle checks on scroll
      cooldownMs: 3 * 60 * 1000, // 3 min cooldown
      dailyCap: 2,                // once per day
      sessionCap: 1,              // once per session
      storageKeyBase: "promo_random_shop",
    });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    axios
      .get(`${REACT_APP_API_URL}/products/families/`, { params: { page_size: 48 } })
      .then((res) => {
        const data = res.data?.results ?? res.data ?? [];
        setFamilies(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Error fetching product families:", err);
      });
  }, [REACT_APP_API_URL]);

  const isLocalDevHost = (host) =>
    /^(localhost|127\.0\.0\.1|0\.0\.0\.0|.+\.local|.+\.test)$/i.test(host || "");

  const toHttpsIfNeeded = (u) => {
    try {
      const url = new URL(u);
      const pageIsHttps = typeof window !== "undefined" && window.location.protocol === "https:";
      if (pageIsHttps && url.protocol === "http:" && !isLocalDevHost(url.hostname)) {
        url.protocol = "https:";
      }
      return url.toString();
    } catch {
      return u;
    }
  };

  const joinPath = (base, path) => {
    const b = String(base || "").replace(/\/+$/, "");
    const p = String(path || "").replace(/^\/+/, "");
    return `${b}/${p}`;
  };

  const asUrl = (path) => {
    if (!path) return "/default.jpg";

    // Absolute URL: maybe upgrade depending on host
    if (/^https?:\/\//i.test(path)) {
      return toHttpsIfNeeded(path);
    }

    // Relative path: build from your image base
    const base = String(REACT_API_IMAGE_URL || "").replace(/\/+$/, "");
    const rel  = String(path).replace(/^\/+/, "");
    return toHttpsIfNeeded(`${base}/${rel}`);
  };
  
  const pickPrimaryImage = (media = []) => {
    const img = media.find((m) => m.media_type === "image" && m.image)?.image;
    if (img) return asUrl(img);
    const poster = media.find((m) => m.poster)?.poster;
    if (poster) return asUrl(poster);
    return "/default.jpg";
  };

  return (
    <div>
      <Layout>
          <Helmet prioritizeSeoTags>
            {/* Basic SEO */}
            <title>Herbal Hair Growth Oil for Fast Hair Growth | Molu Hair Growth</title>
            <meta
              name="description"
              content="Experience unbelievable hair growth with Molu Hair Growth’s herbal hair growth oil — the natural way to achieve thicker, healthier hair."
            />
            <meta
              name="keywords"
              content="herbal hair growth oil, best way to grow hair fast"
            />
            <link
              rel="canonical"
              href={
                (typeof window !== "undefined" ? window.location.origin : "https://molu.co") +
                "/shop"
              }
            />

            {/* Open Graph */}
            <meta property="og:type" content="website" />
            <meta property="og:title" content="Herbal Hair Growth Oil for Fast Hair Growth | Molu Hair Growth" />
            <meta property="og:description" content="Experience unbelievable hair growth with Molu Hair Growth’s herbal hair growth oil — the natural way to achieve thicker, healthier hair." />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:url" content={
              (typeof window !== "undefined" ? window.location.href : "")
            } />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Herbal Hair Growth Oil for Fast Hair Growth | Molu Hair Growth" />
            <meta name="twitter:description" content="Experience unbelievable hair growth with Molu Hair Growth’s herbal hair growth oil — the natural way to achieve thicker, healthier hair." />
            <meta name="twitter:image" content={ogImage} />
          </Helmet>
        <div className="container p-t-80">
          <Breadcrumbs />
        </div>

        <section className="full-page-section bg0 p-t-40 p-b-60">
          <div className="container">
            <div className="text-center p-b-20">
              {/* <a
                href="https://www.etsy.com/shop/MoluHairGrowth"
                className="molu-etsy-btn flex-c-m stext-101 cl0 size-121 bg3 bor1 hov-btn2 p-lr-15 trans-04 pointer"
                target="_blank"
                rel="noreferrer"
              >
                Visit our Etsy Shop
              </a> */}
            </div>

            <div className="row isotope-grid">
            {families.map((fam) => {
              const defaultId = String(fam.default_variant_id || "");
              const defVar = fam.variants?.[defaultId];
              const imgUrl = pickPrimaryImage(defVar?.media || []);
              const blurb = fam.blurb || "";

              return (
                <div className="col-12 col-sm-6 col-lg-4 p-b-35 isotope-item d-flex box-shadow-below" key={fam.slug || defaultId}>
                  <div
                    className="block2 molu-card h-100 d-flex flex-column hov-img0 w-100"
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/shop/product/${defaultId}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/shop/product/${defaultId}`);
                      }
                    }}
                  >
                    <div className="block2-pic">
                      <img src={imgUrl} alt={fam.name} className="product-img-size" />
                    </div>

                    <div className="block2-txt p-t-14 d-flex flex-column align-items-center text-center flex-grow-1">
                      {/* Uniform text box */}
                      <div className="molu-textbox d-flex flex-column align-items-center text-center w-100">
                        <p className="molu-name fw-bold mb-2">{fam.name}</p>

                        {blurb && (
                          <p className="molu-blurb mb-3 px-2">
                            {blurb}
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/shop/product/${defaultId}`);
                        }}
                        className="explore-btn mt-auto mb-3 px-3 py-2 fw-semibold"
                        aria-label={`Explore ${fam.name}`}
                      >
                        Explore
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            </div>

          </div>
          <PromoOffer
            open={promoOpen}
            onOpenChange={setPromoOpen}
            tabText="Get 20% Off"
            imageAlt="New customer discount"
          />
        </section>

        <section className="bg-light py-5 border-top">
          <div className="container text-center">
            <h2 className="fw-bold mb-3">Why Choose Molu?</h2>
            <p className="lead text-muted mb-4">
              Molu is more than just hair products — it's science-backed wellness. Our formulas use powerful ingredients like <strong>2-Deoxy-D-Ribose</strong> to energize and restore hair follicles naturally.
            </p>

            <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
              <Link to="/start/hair-health-assessment" className="btn btn-warning text-white px-4 py-2 fw-semibold">
                Start Your Hair Assessment
              </Link>
              <Link to="/ten-power-foods" className="btn btn-outline-dark px-4 py-2 fw-semibold">
                Learn About Hair Growth Nutrition
              </Link>
            </div>

            <div className="text-muted mt-4 small">
              ✅ Backed by science & supported by a 90-day money-back guarantee
            </div>
          </div>
        </section>
      </Layout>
    </div>
  );
};

export default Shop;
